import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/nextjs";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { AdminUser } from "admin/types/adminUser";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { AdminUserRoleNumber } from "../constants/adminUserRoles";
import { API_BASE_PATH } from "../constants/constants";
import { RootState } from "admin/store";

const key = "currentAdminUser";
const adapter = createEntityAdapter<AdminUser.AdminUser>();

const initialStateValues: AdminUser.AdminUserState = {
  adminUser: null,
  errors: null,
  loading: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const authAdminUser = createAsyncThunk<
  {
    adminUser: AdminUser.AdminUser;
  },
  {
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/authAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { headers, isServerSide = false, locale } = args;
    // console.log(headers);
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/manager/current_admin_user.json`,
      { headers },
    );
    console.log(result);
    return {
      adminUser: result.data.adminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

interface SignInParams {
  email: string;
  password: string;
}

interface InvitationAcceptanceParams {
  mail_authentication_token: string;
  password: string;
  password_confirmation: string;
}

export const signInAdminUser = createAsyncThunk<
  {
    adminUser: AdminUser.AdminUser;
  },
  { params: SignInParams },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signInAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { params } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/admin_user_auth/sign_in`,
      {
        ...params,
      },
    );
    console.log(result);
    return {
      adminUser: result.data.adminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const signOutAdminUser = createAsyncThunk<
  void,
  void,
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/signOutAdminUser`, async (_, { rejectWithValue }) => {
  try {
    await createWrapper().delete(`${API_BASE_PATH}/admin_user_auth/sign_out`);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postAdminUserInvitationAcceptance = createAsyncThunk<
  void,
  { params: InvitationAcceptanceParams },
  {
    rejectValue: ErrorResponse;
  }
>(
  `${key}/postAdminUserInvitationAcceptance`,
  async (args, { rejectWithValue }) => {
    try {
      const { params } = args;
      await createWrapper().post(
        `${API_BASE_PATH}/admin_users/invitation_acceptance`,
        {
          ...params,
        },
      );
      return;
    } catch (err) {
      return rejectWithValue(err.data.errors);
    }
  },
);

/** slice * */
export const currentAdminUserSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setCurrentAdminUser: (state, action) => {
      state.adminUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        if (action.payload.currentAdminUser?.adminUser) {
          Sentry.configureScope((scope) => {
            scope.setUser({
              id: `${action.payload.currentAdminUser.adminUser.modelNameAndId}`,
            });
          });
        }
        return { ...state, ...action.payload.currentAdminUser };
      })
      .addCase(authAdminUser.fulfilled, (state, action) => {
        state.adminUser = action.payload.adminUser;
      })
      .addCase(signInAdminUser.fulfilled, (state, action) => {
        state.adminUser = action.payload.adminUser;
      })
      .addCase(signOutAdminUser.fulfilled, (state, _) => {
        state.adminUser = null;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const currentAdminUserSelector = createSelector(
  stateSelector,
  (state) => state.adminUser,
);

export const errorSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

// action export
export const { setCurrentAdminUser } = currentAdminUserSlice.actions;

export const canShowAdminUser = (adminUser?: AdminUser.AdminUser) => {
  return adminUser
    ? AdminUserRoleNumber[adminUser.role] <= AdminUserRoleNumber.general
    : false;
};

export const canShowCompany = (adminUser?: AdminUser.AdminUser) => {
  return adminUser
    ? AdminUserRoleNumber[adminUser.role] <= AdminUserRoleNumber.general
    : false;
};

// for Server side
export const redirectPropsIfNotAuthorize = (adminUserResult: {
  adminUser?: AdminUser.AdminUser | null;
  errorCode?: string;
}) => {
  if (!adminUserResult) {
    return {
      redirect: {
        destination: `/manager/signin`,
        permanent: false,
      },
    };
  }
  if (adminUserResult.errorCode === "exception") {
    return {
      redirect: {
        destination: `/manager/signin`,
        permanent: false,
      },
    };
  }
  const adminUser = adminUserResult.adminUser;
  if (!adminUser) {
    return {
      redirect: {
        destination: `/manager/signin`,
        permanent: false,
      },
    };
  }
};

export const redirectPathIfNotAuthorize = (adminUserResult: {
  adminUser?: AdminUser.AdminUser | null;
  errorCode?: string;
}) => {
  if (!adminUserResult) {
    return `/manager/signin`;
  }
  if (adminUserResult.errorCode === "exception") {
    return `/manager/signin`;
  }
  const adminUser = adminUserResult.adminUser;
  if (!adminUser) {
    return `/manager/signin`;
  }
};
